/**
 * Company Page - displays company details and list of projects
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompanyProject } from '../providers/CompanyProjectProvider';
import * as companiesApi from '../services/companiesApi';
import * as projectsApi from '../services/projectsApi';
import { Company } from '../types/company';
import { Project } from '../types/project';

export const CompanyPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { setCurrentCompany, setCurrentProject, refreshProjects } = useCompanyProject();
  
  const [company, setCompany] = useState<Company | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadCompanyData();
    }
  }, [companyId]);

  const loadCompanyData = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);
      
      const [companyData, projectsData] = await Promise.all([
        companiesApi.getCompany(parseInt(companyId)),
        projectsApi.listProjectsByCompany(parseInt(companyId))
      ]);

      setCompany(companyData);
      setProjects(projectsData);
      
      // Set as current company
      await setCurrentCompany(parseInt(companyId));
    } catch (err) {
      console.error('Error loading company data:', err);
      setError('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !newProjectName.trim()) return;

    try {
      setCreating(true);
      const newProject = await projectsApi.createProject(
        parseInt(companyId),
        { name: newProjectName.trim() }
      );
      
      setProjects([...projects, newProject]);
      setNewProjectName('');
      setShowCreateProject(false);
      await refreshProjects();
    } catch (err) {
      console.error('Error creating project:', err);
      alert('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleProjectClick = async (project: Project) => {
    await setCurrentProject(project.id);
    navigate(`/projects/${project.id}/settings`);
  };

  const handleSettingsClick = () => {
    navigate(`/companies/${companyId}/settings`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-xl">{error || 'Company not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 btn btn-primary"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
            <p className="mt-2 text-gray-600">
              {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            </p>
          </div>
          <button
            onClick={handleSettingsClick}
            className="btn btn-secondary"
          >
            Company Settings
          </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Projects</h2>
            {!showCreateProject && (
              <button
                onClick={() => setShowCreateProject(true)}
                className="btn btn-primary"
              >
                + New Project
              </button>
            )}
          </div>

          {showCreateProject && (
            <form onSubmit={handleCreateProject} className="mt-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Project name"
                  className="form-input flex-1"
                  autoFocus
                  required
                />
                <button
                  type="submit"
                  disabled={creating}
                  className="btn btn-primary"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateProject(false);
                    setNewProjectName('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="divide-y divide-gray-200">
          {projects.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No projects yet. Create your first project to get started.</p>
            </div>
          ) : (
            projects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project)}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {project.name}
                    </h3>
                    {project.description && (
                      <p className="mt-1 text-sm text-gray-600">
                        {project.description}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Created {new Date(project.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
